import { apiDelete, apiGet, apiPost } from "../_apiShim";
import type {
  GuestTrashPage,
  GuestTrashPurgeRequest,
  GuestTrashRestoreRequest,
  GuestTrashRestoreResult,
} from "@/types/GuestTrash";

const BASE_PATH = "/api/v1/guests/";
const NO_STORE = { "Cache-Control": "no-store" };

/** `GET /guests/{eventId}/trash?page&size` — the Papierkorb list (snapshot projection, no credentials). */
export async function listTrash(eventId: string, page: number, size: number): Promise<GuestTrashPage> {
  const qs = new URLSearchParams({ page: String(page), size: String(size) });
  return await apiGet<GuestTrashPage>(`${BASE_PATH}${eventId}/trash?${qs.toString()}`, NO_STORE);
}

/**
 * `GET /guests/{eventId}/trash/count` — `{count, enabled}`; `enabled` says
 * whether the trash is on for this event (an older backend answers `{count}`
 * alone). 404 on a backend without the trash (the caller hides the entry).
 */
export async function trashCount(eventId: string): Promise<{ count: number; enabled?: boolean }> {
  return await apiGet<{ count: number; enabled?: boolean }>(`${BASE_PATH}${eventId}/trash/count`, NO_STORE);
}

/**
 * `POST /guests/{eventId}/trash/restore` — `dryRun: true` answers the same shape
 * without writing (the preview). 409 `trashBatchStaging` / `restoreMainGuestMissing`
 * / `restoreGuestLimitExceeded` arrive as thrown errors with that code as message.
 */
export async function restoreTrash(
  eventId: string,
  body: GuestTrashRestoreRequest,
): Promise<GuestTrashRestoreResult> {
  return await apiPost<GuestTrashRestoreResult>(`${BASE_PATH}${eventId}/trash/restore`, body, NO_STORE);
}

/**
 * `DELETE /guests/{eventId}/trash {guestIds|batchId}` — early permanent purge.
 * 200 `{purged, skipped[{guestId, reason}], receiptIds}` for one unit, 202
 * `{purged: 0, queued, skipped}` when the sweeper takes the rest. Who may:
 * guests:delete + row- and field-unrestricted (`GuestTrashAccess`), no AI
 * session — no role floor (FB-8, as before W6).
 */
export async function purgeTrash(eventId: string, body: GuestTrashPurgeRequest): Promise<unknown> {
  return await apiDelete<unknown>(`${BASE_PATH}${eventId}/trash`, NO_STORE, body);
}
